import { auth } from "./init-firebase";

/**
 * firestoreIndexHandler
 *
 * Intercepta erros do Firestore causados por índices ausentes.
 *
 * Fluxo:
 *  1. A query Firestore falha com "The query requires an index..."
 *  2. handleFirestoreError detecta o erro
 *  3. Dispara criação automática do índice via API (best-effort, em segundo plano)
 *  4. Lança IndexRequiredError com mensagem amigável ao usuário
 *
 * O componente que capturar o IndexRequiredError pode exibir a mensagem
 * diretamente — sem precisar de lógica adicional de detecção.
 */

const USER_FRIENDLY_MESSAGE =
  "Esta funcionalidade está sendo configurada e ficará disponível em alguns minutos. " +
  "Por favor, tente novamente em breve.";

/**
 * Erro especializado lançado quando um índice Firestore está ausente.
 * Contém uma mensagem amigável para exibição ao usuário.
 */
export class IndexRequiredError extends Error {
  constructor() {
    super(USER_FRIENDLY_MESSAGE);
    this.name = "IndexRequiredError";
    this.isIndexError = true;
  }
}

/**
 * Verifica se o erro é causado por um índice Firestore ausente.
 * @param {Error|any} error
 * @returns {boolean}
 */
export function isIndexRequiredError(error) {
  if (!error) return false;
  const msg = (error.message || String(error)).toLowerCase();
  return (
    msg.includes("the query requires an index") ||
    msg.includes("requires an index") ||
    (error.code === "failed-precondition" && msg.includes("index"))
  );
}

/**
 * Intercepta erros de índice ausente, dispara a criação automática via API
 * e relança como IndexRequiredError com mensagem amigável.
 *
 * Para todos os outros erros, relança sem modificação.
 *
 * Uso nos serviços de dados:
 *   } catch (error) {
 *     await handleFirestoreError(error);
 *   }
 *
 * @param {Error|any} error
 * @throws {IndexRequiredError} se for erro de índice ausente
 * @throws {Error} para qualquer outro tipo de erro
 */
export async function handleFirestoreError(error) {
  if (!isIndexRequiredError(error)) {
    throw error;
  }

  console.warn(
    "[firestoreIndexHandler] Índice ausente detectado. Solicitando criação automática...",
    error.message
  );

  // Dispara a criação do índice em segundo plano via fetch nativo (não passa pelo
  // interceptor do axios, evitando logs de erro desnecessários no console do browser).
  // - Funciona para usuários administradores (o endpoint exige admin).
  // - Para usuários comuns, a chamada retorna 403 e é descartada silenciosamente.
  // - Em ambos os casos, o usuário vê a mensagem amigável abaixo.
  (async () => {
    try {
      let token = null;
      if (auth?.currentUser) {
        token = await auth.currentUser.getIdToken();
      }
      const response = await fetch("/api/admin/firestore-indexes/auto-create", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ errorMessage: error.message }),
      });
      if (response.ok) {
        const data = await response.json();
        console.info("[firestoreIndexHandler] Criação de índice iniciada:", data?.message);
      }
    } catch (_) {
      // Silencia qualquer erro (rede, emulador não disponível, etc.)
    }
  })();

  throw new IndexRequiredError();
}
